/**
 * The signed-in human as the UI presents them.
 *
 * Two domains, two sources, never mixed:
 *  - WHO they are (name) is account identity — the Auth profile
 *    (GET /identity/me), self-readable by every signed-in person. The name
 *    rule is the backend directory's own: displayName, else username. The
 *    `spec_…` uid is a database identifier and is never shown as a name.
 *  - WHAT they are at this clinic (job title, specialty) is Clinic domain —
 *    the cms-backend personnel profile (GET /v1/personnel/:uid). It is
 *    readable only behind clinic.personnel.read; there is no self route, so
 *    for a member without that key the role is honestly "unreadable", never
 *    guessed from permission keys and never taken from Auth's RBAC roles.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchMyProfile } from './api';
import { useSession } from './SessionProvider';
import { useAuthz } from '../authz/AuthzProvider';
import { PERM } from '../authz/permissions';
import { qk } from '../api/keys';
import { fetchPersonnel, type PersonnelView } from '../clinic/setupApi';
import { displayNameOf, initialsOf } from './identity';

export function useMyIdentity(): {
  uid: string | null;
  /** displayName, else username, else null — never the uid. */
  name: string | null;
  initials: string;
  loading: boolean;
} {
  const { session } = useSession();
  const uid = session.status === 'signedIn' ? session.me.uid : null;
  const q = useQuery({
    queryKey: uid ? qk.profile(uid) : ['profile', 'none'],
    queryFn: () => fetchMyProfile(),
    enabled: uid !== null,
    staleTime: 10 * 60_000,
  });
  const name = displayNameOf(q.data);
  return { uid, name, initials: initialsOf(name), loading: uid !== null && q.isPending };
}

export type MyPersonnelState =
  /** No clinic.personnel.read: the profile exists but this member cannot read it. */
  | { status: 'unreadable' }
  | { status: 'loading' }
  | { status: 'error'; retry: () => void }
  /** `view` is null when this member has no personnel profile at this clinic (PERSONNEL_NOT_FOUND). */
  | { status: 'ready'; view: PersonnelView | null };

/**
 * The signed-in member's own personnel profile at the selected clinic — one
 * cached read shared with useMyPractitioner (same key), asked only when the
 * clinic would answer.
 */
export function useMyPersonnel(): MyPersonnelState {
  const authz = useAuthz();
  const { session } = useSession();
  const uid = session.status === 'signedIn' ? session.me.uid : null;
  const enabled = uid !== null && authz.clinicId !== null && authz.can(PERM.personnelRead);
  const q = useQuery({
    queryKey: ['personnel', authz.clinicId, uid],
    queryFn: () => fetchPersonnel(uid!),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
  if (!enabled) return { status: 'unreadable' };
  if (q.isPending) return { status: 'loading' };
  if (q.isError) {
    // A 404 is a fact, not a failure: this member simply has no staff profile here.
    const status = (q.error as { status?: number }).status;
    if (status === 404) return { status: 'ready', view: null };
    return { status: 'error', retry: () => void q.refetch() };
  }
  return { status: 'ready', view: q.data };
}

/** The clinic role line the product already uses for a staff member: the job title. */
export function clinicRoleOf(view: PersonnelView | null | undefined): string | null {
  const title = view?.jobTitle?.trim();
  return title ? title : null;
}
